import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI, transactionsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import LangSwitch from '../components/LangSwitch';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, Pencil, Shield, Eye, TrendingUp, Wallet, Calendar } from 'lucide-react';

export default function AdminClientEdit() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [showAddTx, setShowAddTx] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [newTx, setNewTx] = useState({ type: 'withdrawal', date: '', amount: 0, note: '' });
  const [allocations, setAllocations] = useState([]);

  const loadClient = useCallback(async () => {
    try {
      const res = await clientsAPI.get(clientId);
      setClient(res.data);
      setForm({
        client_name: res.data.client_name || '',
        deposit: res.data.deposit || 0,
        balance: res.data.balance || 0,
        balance_date: res.data.balance_date || '',
        return_amount: res.data.return_amount || 0,
        return_pct: res.data.return_pct || 0,
        monthly_return_pct: res.data.monthly_return_pct || 0,
        account_type: res.data.account_type || '',
        date_joined: res.data.date_joined || '',
        new_balance: res.data.new_balance || 0,
        new_balance_date: res.data.new_balance_date || '',
      });
      setAllocations(res.data.allocations || []);
    } catch {
      toast.error(t('edit_not_found'));
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [clientId, navigate, t]);

  useEffect(() => { loadClient(); }, [loadClient]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await clientsAPI.update(clientId, { ...form, allocations });
      toast.success(t('edit_client_updated'));
      loadClient();
    } catch { toast.error(t('edit_save_error')); }
    finally { setSaving(false); }
  };

  const handleAddTx = async () => {
    try {
      await transactionsAPI.add(clientId, newTx);
      toast.success(t('edit_tx_added'));
      setShowAddTx(false);
      setNewTx({ type: 'withdrawal', date: '', amount: 0, note: '' });
      loadClient();
    } catch { toast.error(t('common_error')); }
  };

  const handleUpdateTx = async () => {
    try {
      await transactionsAPI.update(clientId, editTx.id, editTx);
      toast.success(t('edit_tx_updated'));
      setEditTx(null);
      loadClient();
    } catch { toast.error(t('common_error')); }
  };

  const handleDeleteTx = async (txId) => {
    if (!window.confirm(t('edit_confirm_delete_tx'))) return;
    try {
      await transactionsAPI.delete(clientId, txId);
      toast.success(t('edit_tx_deleted'));
      loadClient();
    } catch { toast.error(t('common_error')); }
  };

  const addAllocation = () => setAllocations([...allocations, { name: '', percentage: 0 }]);
  const removeAllocation = (i) => setAllocations(allocations.filter((_, idx) => idx !== i));
  const updateAllocation = (i, field, val) => {
    const updated = [...allocations];
    updated[i] = { ...updated[i], [field]: field === 'percentage' ? parseFloat(val) || 0 : val };
    setAllocations(updated);
  };

  const fmt = (n) => new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'USD' }).format(n);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0C15' }}>
      <div className="text-[#94A3B8]">{t('common_loading')}</div>
    </div>
  );

  const sortedTx = [...(client?.transactions || [])].sort((a, b) => {
    const [dA, mA, yA] = a.date.split('/').map(Number);
    const [dB, mB, yB] = b.date.split('/').map(Number);
    return new Date(yA, mA - 1, dA) - new Date(yB, mB - 1, dB);
  });

  return (
    <div className="min-h-screen" style={{ background: '#0B0C15' }} data-testid="admin-client-edit">
      <header className="border-b border-[#2D2E3D] bg-[#151621]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button data-testid="back-to-dashboard" variant="ghost" size="icon" className="text-[#94A3B8] hover:text-white" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white">{t('edit_edition')}: {client?.client_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Button data-testid="view-report-button" variant="ghost" size="sm" className="text-[#0EA5E9]" onClick={() => navigate(`/report/${clientId}`)}>
              <Eye className="w-4 h-4 mr-1" /> {t('edit_view_report')}
            </Button>
            <Button data-testid="save-client-button" size="sm" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-1" /> {saving ? t('edit_saving') : t('edit_save')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="info" className="space-y-6">
          <TabsList className="bg-[#151621] border border-[#2D2E3D] p-1">
            <TabsTrigger data-testid="tab-info" value="info" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white text-[#94A3B8]">{t('edit_tab_info')}</TabsTrigger>
            <TabsTrigger data-testid="tab-transactions" value="transactions" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white text-[#94A3B8]">{t('edit_tab_transactions')} ({client?.transactions?.length || 0})</TabsTrigger>
            <TabsTrigger data-testid="tab-allocations" value="allocations" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white text-[#94A3B8]">{t('edit_tab_allocations')}</TabsTrigger>
          </TabsList>

          <TabsContent value="info">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-[#151621] border-[#2D2E3D]">
                <CardHeader className="border-b border-[#2D2E3D]/50">
                  <CardTitle className="text-base text-white flex items-center gap-2"><Wallet className="w-4 h-4 text-[#7C3AED]" /> {t('edit_client_info')}</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_client_name')}</label>
                    <Input data-testid="edit-client-name" className="bg-[#1E1F2E] border-[#2D2E3D] text-white" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_account_type')}</label>
                    <Input data-testid="edit-account-type" className="bg-[#1E1F2E] border-[#2D2E3D] text-white text-xs" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_date_joined')}</label>
                    <Input data-testid="edit-date-joined" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.date_joined} onChange={(e) => setForm({ ...form, date_joined: e.target.value })} />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#151621] border-[#2D2E3D]">
                <CardHeader className="border-b border-[#2D2E3D]/50">
                  <CardTitle className="text-base text-white flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#10B981]" /> {t('edit_performance')}</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_deposit')}</label><Input data-testid="edit-deposit" type="number" step="0.01" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: parseFloat(e.target.value) || 0 })} /></div>
                    <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_balance')}</label><Input data-testid="edit-balance" type="number" step="0.01" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.balance} onChange={(e) => setForm({ ...form, balance: parseFloat(e.target.value) || 0 })} /></div>
                  </div>
                  <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_balance_date')}</label><Input data-testid="edit-balance-date" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.balance_date} onChange={(e) => setForm({ ...form, balance_date: e.target.value })} /></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_return_amount')}</label><Input data-testid="edit-return-amount" type="number" step="0.01" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.return_amount} onChange={(e) => setForm({ ...form, return_amount: parseFloat(e.target.value) || 0 })} /></div>
                    <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_cumul_pct')}</label><Input data-testid="edit-return-pct" type="number" step="0.01" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.return_pct} onChange={(e) => setForm({ ...form, return_pct: parseFloat(e.target.value) || 0 })} /></div>
                    <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_monthly_pct')}</label><Input data-testid="edit-monthly-return-pct" type="number" step="0.01" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.monthly_return_pct} onChange={(e) => setForm({ ...form, monthly_return_pct: parseFloat(e.target.value) || 0 })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_new_balance')}</label><Input data-testid="edit-new-balance" type="number" step="0.01" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.new_balance} onChange={(e) => setForm({ ...form, new_balance: parseFloat(e.target.value) || 0 })} /></div>
                    <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_new_balance_date')}</label><Input data-testid="edit-new-balance-date" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={form.new_balance_date} onChange={(e) => setForm({ ...form, new_balance_date: e.target.value })} /></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="transactions">
            <Card className="bg-[#151621] border-[#2D2E3D]">
              <CardHeader className="border-b border-[#2D2E3D]/50 flex-row items-center justify-between">
                <CardTitle className="text-base text-white flex items-center gap-2"><Calendar className="w-4 h-4 text-[#0EA5E9]" /> {t('edit_tx_history')}</CardTitle>
                <Button data-testid="add-transaction-button" size="sm" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs" onClick={() => setShowAddTx(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> {t('edit_add')}
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#1E1F2E] hover:bg-[#1E1F2E] border-[#2D2E3D]/50">
                      <TableHead className="text-[#94A3B8] font-medium px-6 py-3">{t('edit_date')}</TableHead>
                      <TableHead className="text-[#94A3B8] font-medium">{t('edit_type')}</TableHead>
                      <TableHead className="text-[#94A3B8] font-medium">{t('edit_amount')}</TableHead>
                      <TableHead className="text-[#94A3B8] font-medium">{t('edit_note')}</TableHead>
                      <TableHead className="text-[#94A3B8] font-medium text-right pr-6">{t('admin_actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedTx.map((tx) => (
                      <TableRow key={tx.id} className="border-[#2D2E3D]/50 hover:bg-[#1E1F2E]/50">
                        <TableCell className="px-6 py-3 font-mono text-sm text-white">{tx.date}</TableCell>
                        <TableCell>
                          <Badge className={tx.type === 'deposit' ? 'bg-[#10B981]/20 text-[#10B981] border-0 text-xs' : 'bg-[#F59E0B]/20 text-[#F59E0B] border-0 text-xs'}>
                            {tx.type === 'deposit' ? t('edit_tx_deposit') : t('edit_tx_withdrawal')}
                          </Badge>
                        </TableCell>
                        <TableCell className={`font-mono text-sm ${tx.type === 'deposit' ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                          {tx.type === 'deposit' ? '+' : '-'}{fmt(tx.amount)}
                        </TableCell>
                        <TableCell className="text-sm text-[#94A3B8]">{tx.note || '—'}</TableCell>
                        <TableCell className="text-right pr-6">
                          <div className="flex items-center justify-end gap-1">
                            <Button data-testid={`edit-tx-${tx.id}`} variant="ghost" size="icon" className="h-7 w-7 text-[#64748B] hover:text-white" onClick={() => setEditTx({ ...tx })}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button data-testid={`delete-tx-${tx.id}`} variant="ghost" size="icon" className="h-7 w-7 text-[#64748B] hover:text-red-400" onClick={() => handleDeleteTx(tx.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="allocations">
            <Card className="bg-[#151621] border-[#2D2E3D]">
              <CardHeader className="border-b border-[#2D2E3D]/50 flex-row items-center justify-between">
                <CardTitle className="text-base text-white">{t('edit_allocations')}</CardTitle>
                <Button data-testid="add-allocation-button" size="sm" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs" onClick={addAllocation}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> {t('edit_add')}
                </Button>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {allocations.map((a, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Input data-testid={`allocation-name-${i}`} className="bg-[#1E1F2E] border-[#2D2E3D] text-white flex-1" placeholder={t('edit_alloc_placeholder')} value={a.name} onChange={(e) => updateAllocation(i, 'name', e.target.value)} />
                      <div className="flex items-center gap-2 w-32">
                        <Input data-testid={`allocation-pct-${i}`} type="number" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono w-20" value={a.percentage} onChange={(e) => updateAllocation(i, 'percentage', e.target.value)} />
                        <span className="text-[#94A3B8] text-sm">%</span>
                      </div>
                      <Button data-testid={`remove-allocation-${i}`} variant="ghost" size="icon" className="h-8 w-8 text-[#64748B] hover:text-red-400" onClick={() => removeAllocation(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                  {allocations.length === 0 && <p className="text-[#64748B] text-sm text-center py-6">{t('edit_no_alloc')}</p>}
                  {allocations.length > 0 && (
                    <div className="pt-3 border-t border-[#2D2E3D]/50 flex justify-between">
                      <span className="text-sm text-[#94A3B8]">{t('edit_total')}</span>
                      <span className={`font-mono text-sm font-semibold ${allocations.reduce((s, a) => s + a.percentage, 0) === 100 ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                        {allocations.reduce((s, a) => s + a.percentage, 0)}%
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Tx Dialog */}
      <Dialog open={showAddTx} onOpenChange={setShowAddTx}>
        <DialogContent className="bg-[#151621] border-[#2D2E3D] text-white max-w-md">
          <DialogHeader><DialogTitle>{t('edit_new_tx')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_type')}</label>
              <select data-testid="new-tx-type" className="w-full h-9 rounded-md border border-[#2D2E3D] bg-[#1E1F2E] px-3 text-sm text-white" value={newTx.type} onChange={(e) => setNewTx({ ...newTx, type: e.target.value })}>
                <option value="withdrawal">{t('edit_tx_withdrawal')}</option><option value="deposit">{t('edit_tx_deposit')}</option>
              </select></div>
            <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_date')} (DD/MM/YYYY)</label><Input data-testid="new-tx-date" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" placeholder="01/01/2026" value={newTx.date} onChange={(e) => setNewTx({ ...newTx, date: e.target.value })} /></div>
            <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_amount')} ($)</label><Input data-testid="new-tx-amount" type="number" step="0.01" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={newTx.amount} onChange={(e) => setNewTx({ ...newTx, amount: parseFloat(e.target.value) || 0 })} /></div>
            <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_note')}</label><Input data-testid="new-tx-note" className="bg-[#1E1F2E] border-[#2D2E3D] text-white" value={newTx.note} onChange={(e) => setNewTx({ ...newTx, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-[#94A3B8]" onClick={() => setShowAddTx(false)}>{t('admin_cancel')}</Button>
            <Button data-testid="confirm-add-tx" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={handleAddTx}>{t('edit_add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tx Dialog */}
      <Dialog open={!!editTx} onOpenChange={() => setEditTx(null)}>
        <DialogContent className="bg-[#151621] border-[#2D2E3D] text-white max-w-md">
          <DialogHeader><DialogTitle>{t('edit_edit_tx')}</DialogTitle></DialogHeader>
          {editTx && (
            <div className="space-y-4">
              <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_type')}</label>
                <select data-testid="edit-tx-type" className="w-full h-9 rounded-md border border-[#2D2E3D] bg-[#1E1F2E] px-3 text-sm text-white" value={editTx.type} onChange={(e) => setEditTx({ ...editTx, type: e.target.value })}>
                  <option value="withdrawal">{t('edit_tx_withdrawal')}</option><option value="deposit">{t('edit_tx_deposit')}</option>
                </select></div>
              <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_date')}</label><Input data-testid="edit-tx-date" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={editTx.date} onChange={(e) => setEditTx({ ...editTx, date: e.target.value })} /></div>
              <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_amount')} ($)</label><Input data-testid="edit-tx-amount" type="number" step="0.01" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={editTx.amount} onChange={(e) => setEditTx({ ...editTx, amount: parseFloat(e.target.value) || 0 })} /></div>
              <div><label className="text-sm text-[#94A3B8] mb-1 block">{t('edit_note')}</label><Input data-testid="edit-tx-note" className="bg-[#1E1F2E] border-[#2D2E3D] text-white" value={editTx.note || ''} onChange={(e) => setEditTx({ ...editTx, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="text-[#94A3B8]" onClick={() => setEditTx(null)}>{t('admin_cancel')}</Button>
            <Button data-testid="confirm-edit-tx" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={handleUpdateTx}>{t('edit_save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
